import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import CountUp from "react-countup";
import axios from "axios";
import {
  ArrowRight,
  Users,
  Layers,
  Gem,
  Zap,
  Lightbulb,
  RefreshCw,
  Star,
  CheckCircle2,
  FileText,
  FolderArchive,
  Download,
} from "lucide-react";
import {
  aboutData,
  services as mockServices,
  principles,
  customerRating,
  stats as mockStats,
  whyChooseUs,
  documentsData,
} from "../data/mockData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = { Layers, Gem, Zap, Lightbulb, RefreshCw };
const docIconMap = { FileText, FolderArchive };

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const SectionHeader = ({ subtitle, title, light }) => (
  <div className="section-header">
    {subtitle && (
      <span className={`section-subtitle ${light ? "light" : ""}`}>{subtitle}</span>
    )}
    <h2 className={`section-title ${light ? "light" : ""}`}>{title}</h2>
    <div className="section-line" />
  </div>
);

/* ===== ABOUT SECTION ===== */
export const AboutSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  return (
    <section id="about" className="about-section" ref={ref}>
      <div className="about-inner">
        <div className="about-left">
          <motion.div
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={fadeUp}
          >
            <span className="about-subtitle">{aboutData.subtitle}</span>
            <h2 className="about-title">{aboutData.title}</h2>
            <div className="section-line light-line" />
            <p className="about-desc">{aboutData.description}</p>
            <Link to="/about" className="btn-outline-light">
              <Users size={16} />
              VIEW OUR TEAM
            </Link>
          </motion.div>
        </div>
        <div className="about-right">
          <motion.div
            className="about-highlight-box"
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <h3 className="highlight-title">{aboutData.highlight}</h3>
            <div className="highlight-years">
              <span className="years-number">
                {inView && <CountUp end={aboutData.yearsExperience} duration={2} />}
              </span>
              <span className="years-label">
                Years of
                <br />
                Experience
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ===== SERVICES SECTION ===== */
export const ServicesSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [services, setServices] = useState(mockServices);

  useEffect(() => {
    axios.get(`${API}/services`).then(res => {
      if (res.data && res.data.length > 0) setServices(res.data);
    }).catch(() => {});
  }, []);
  return (
    <section id="services" className="services-section" ref={ref}>
      <div className="services-inner">
        {services.map((service, i) => {
          const IconComp = iconMap[service.icon] || Layers;
          return (
            <motion.div
              key={service.id}
              className="service-card"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.15 }}
            >
              <div className="service-icon">
                <IconComp size={40} strokeWidth={1.5} />
              </div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-desc">{service.shortDesc}</p>
              <Link to={service.link} className="service-link">
                More Details <ArrowRight size={16} />
              </Link>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

/* ===== WHY CHOOSE US SECTION ===== */
export const WhyChooseUsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  return (
    <section className="why-choose-section" ref={ref}>
      <div
        className="why-choose-bg"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1576153192396-180ecef2a715?w=1920&h=600&fit=crop')",
        }}
      />
      <div className="why-choose-overlay" />
      <div className="why-choose-inner">
        <motion.div
          className="wc-left"
          initial={{ opacity: 0, x: -30 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="section-subtitle light">{whyChooseUs.title}</span>
          <h2 className="section-title light" style={{ marginBottom: 16 }}>
            DFAM-Optimized Additive Manufacturing
          </h2>
          <div className="section-line" />
          <p className="wc-desc">{whyChooseUs.description}</p>
          <div className="wc-points">
            {whyChooseUs.points.map((point, i) => (
              <motion.div key={i} className="wc-point"
                initial={{ opacity: 0, y: 15 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.3 + i * 0.15 }}>
                <CheckCircle2 size={20} />
                <div>
                  <h4>{point.title}</h4>
                  <p>{point.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
        <motion.div
          className="wc-right"
          initial={{ opacity: 0, x: 30 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="rating-box">
            <div className="rating-score">
              {inView && <CountUp end={customerRating.score} decimals={1} duration={2} />}
            </div>
            <div className="rating-label">{customerRating.label}</div>
            <div className="rating-stars">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} fill="#c54b2a" stroke="#c54b2a" />
              ))}
            </div>
            <div className="rating-review">{customerRating.review}</div>
          </div>
          <div className="rating-vertical-text">FULL REVIEWS AT GOOGLE BUSINESS</div>
        </motion.div>
      </div>
    </section>
  );
};

/* ===== CORE VALUES SECTION (was Principles) ===== */
export const CoreValuesSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <section className="principles-section" ref={ref}>
      <div className="principles-inner">
        <motion.div
          className="principles-left"
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={fadeUp}
        >
          <SectionHeader title="Our Core Values" />
          <p className="principles-desc">
            Since 2018, 3D Printzkart has built lasting partnerships
            with startups, enterprises, and research institutions. As a diversified
            additive manufacturing service provider in Bangalore, we are recognized as one
            of India's most trusted 3D printing companies, serving clients across 20+
            countries.
          </p>
        </motion.div>
        <div className="principles-right">
          <div className="principles-grid">
            {principles.map((p, i) => (
              <motion.div
                key={i}
                className="principle-card"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.15 }}
              >
                <div className="principle-number">{p.number}</div>
                <h3 className="principle-title">{p.title}</h3>
                <p className="principle-desc">{p.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ===== DOCUMENTS SECTION ===== */
export const DocumentsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  return (
    <section className="documents-section" ref={ref}>
      <div className="documents-inner">
        <motion.div initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeUp}>
          <SectionHeader title="Documents" />
        </motion.div>
        <div className="documents-grid">
          {documentsData.map((doc, i) => {
            const DocIcon = docIconMap[doc.icon] || FileText;
            return (
              <motion.a
                key={i}
                href={doc.link || "#"}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="document-card"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: i * 0.15 }}
              >
                <DocIcon size={24} />
                <div className="doc-info">
                  <span className="doc-title">{doc.title}</span>
                  <span className="doc-format">({doc.format})</span>
                </div>
                <Download size={18} className="doc-download" />
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ===== STATS SECTION ===== */
export const StatsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [stats, setStats] = useState(mockStats);

  useEffect(() => {
    axios.get(`${API}/stats`).then(res => {
      if (res.data && res.data.length > 0) setStats(res.data);
    }).catch(() => {});
  }, []);
  return (
    <section className="stats-section" ref={ref}>
      <div className="stats-inner">
        {stats.map((stat, i) => (
          <motion.div
            key={i}
            className="stat-item"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.4, delay: i * 0.1 }}
          >
            <div className="stat-value">
              {inView && <CountUp end={stat.value} duration={2.5} separator="," />}
              {stat.suffix}
            </div>
            <div className="stat-label">{stat.label}</div>
            <div className="stat-sublabel">{stat.sublabel}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};
